import { Link, useRouter } from "@shared/client/navigation";
import { useState } from "react";
import { BadgeEuro, LoaderCircle } from "lucide-react";
import type { InvoiceRecord } from "@shared/lib/admin-db";
import { formatCents, formatDate } from "@shared/lib/format";
import { AdminEmpty, AdminPanel } from "@shared/components/blocks";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";

const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: "Brouillon",
  sent: "Envoyée",
  paid: "Payée",
  overdue: "En retard",
  void: "Annulée",
};

/** Factures émises à la main pour un client (hors Stripe). */
export function ManualInvoices({
  invoices,
  clients,
  className,
}: {
  invoices: InvoiceRecord[];
  clients: Array<{ id: string; name: string }>;
  className?: string;
}) {
  const router = useRouter();
  const [clientId, setClientId] = useState(clients[0]?.id ?? "");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function createInvoice(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/admin/invoices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: clientId,
          amount_cents: Math.round(Number(amount.replace(",", ".")) * 100) || 0,
          description: description || undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Création impossible.");
      setAmount("");
      setDescription("");
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Création impossible.");
    } finally {
      setBusy(false);
    }
  }

  async function setInvoiceStatus(id: string, status: string) {
    setError("");
    try {
      const response = await fetch(`/api/admin/invoices/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Mise à jour impossible.");
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Mise à jour impossible.");
    }
  }

  return (
    <AdminPanel className={className} flush title="Factures manuelles">
      {clients.length > 0 && (
        <form className="flex flex-wrap items-center gap-3 border-b bg-muted/40 px-4 py-3.5" onSubmit={createInvoice}>
          <Select onValueChange={setClientId} value={clientId}>
            <SelectTrigger aria-label="Client à facturer" className="h-9 min-w-[200px] bg-background text-xs font-semibold">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            className="h-9 max-w-[160px] bg-background"
            inputMode="decimal"
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Montant (€)"
            required
            value={amount}
          />
          <Input
            className="h-9 max-w-[280px] bg-background"
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description"
            value={description}
          />
          <Button disabled={busy || !clientId} size="sm" type="submit">
            {busy ? <LoaderCircle className="animate-spin" /> : <BadgeEuro />} Créer la facture
          </Button>
        </form>
      )}

      {error && (
        <Alert className="m-4 w-auto" role="alert" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {invoices.length === 0 ? (
        <AdminEmpty>Aucune facture manuelle. Les abonnements sont facturés par Stripe.</AdminEmpty>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="pl-5">Numéro</TableHead>
              <TableHead className="max-sm:hidden">Client</TableHead>
              <TableHead className="text-right">Montant</TableHead>
              <TableHead className="max-sm:hidden">Émise le</TableHead>
              <TableHead className="pr-5">Statut</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell className="pl-5">
                  <strong className="font-semibold">{invoice.number}</strong>
                  {invoice.description && (
                    <small className="block text-[12px] text-muted-foreground">{invoice.description}</small>
                  )}
                </TableCell>
                <TableCell className="max-sm:hidden">
                  {invoice.client ? (
                    <Link className="hover:text-foreground" href={`/admin/clients/${invoice.client.id}`}>
                      {invoice.client.name}
                    </Link>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatCents(invoice.amount_cents)}</TableCell>
                <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums max-sm:hidden">
                  {formatDate(invoice.issued_at)}
                </TableCell>
                <TableCell className="pr-5">
                  <Select onValueChange={(value) => setInvoiceStatus(invoice.id, value)} value={invoice.status}>
                    <SelectTrigger aria-label={`Statut de la facture ${invoice.number}`} className="h-8 text-xs" size="sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(INVOICE_STATUS_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </AdminPanel>
  );
}
